import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Backdrop, Box, Portal, Stack, Tooltip, useTheme } from '@mui/material'
import PropTypes from 'prop-types'
import { ActionButton, Picto, SubmitButton } from '~/components'
import { useFocusTrap } from '~/hooks'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'
import { getSaveData, useParagraphContext } from './hooks'
import RestoreFromParentDialog from './RestoreFromParentDialog'

const Field = (props) => {
  const {
    children,
    editable = true,
    name,
    popupMode = true,
    render,
    shift = true,
    submitOnEnter = true,
    ...rest
  } = props
  const { t } = useTranslation()
  const { product } = useRouteContext(Field.name)
  const {
    paragraph,
    diffFromParent,
    restoreToParent,
    isDirty,
    onPropertyChange,
    resetProperty,
    saveParagraph,
  } = useParagraphContext(Field.name)
  const theme = useTheme()
  const anchorRef = useRef(null)
  const placeholderRef = useRef(null)
  const [dimensions, setDimensions] = useState(null)
  const [storedRect, setStoredRect] = useState(null)
  const [position, setPosition] = useState(null)
  const dirty = Boolean(name && isDirty(name))
  const [saving, setSaving] = useState(false)
  const [restoreDiff, setRestoreDiff] = useState(null)
  const interaction = useSelector(bshSlice.interaction)
  const isPopupMode = useMemo(
    () => popupMode && interaction === 'buttons' && dirty && !saving,
    [popupMode, interaction, dirty, saving]
  )
  const elevatedRect = isPopupMode ? (position ?? storedRect) : null

  const restorableField = useMemo(() => {
    if (!name || !editable) return null
    if (name.startsWith('shops.')) {
      const [, shop, field] = name.split('.')
      if (paragraph.shops?.[shop]?.custom_fields?.includes(field)) {
        return name
      }
    } else if (paragraph.custom_fields?.includes(name)) {
      return name
    }
    return null
  }, [name, editable, paragraph.custom_fields, paragraph.shops])

  const handlePictoClick = useCallback(async () => {
    if (!restorableField) return
    const diff = await diffFromParent([restorableField])
    if (diff) {
      setRestoreDiff(diff)
    }
  }, [diffFromParent, restorableField])

  const handleRestoreConfirm = useCallback(async () => {
    if (!restorableField) return
    await restoreToParent([restorableField])
  }, [restorableField, restoreToParent])

  const picto = useMemo(() => {
    if (!name) return null

    const fieldName = name.split('.').pop().replace('_', '-')

    return restorableField ? (
      <Box sx={shift ? { mt: 1 } : {}}>
        <Tooltip
          title={t('paragraph.restore-to-parent', {
            fields: JSON.stringify(t(`paragraph.${fieldName}`).toLowerCase()),
          })}
        >
          <Picto letter="M" onClick={handlePictoClick} />
        </Tooltip>
      </Box>
    ) : null
  }, [name, restorableField, shift, t, handlePictoClick])

  const save = useCallback(
    async (overwrite = false) => {
      if (!dirty) return
      setSaving(true)
      await saveParagraph(
        getSaveData(name, paragraph),
        overwrite || interaction === 'overwrite'
      )
      setSaving(false)
    },
    [dirty, interaction, name, paragraph, saveParagraph]
  )

  const onBlur = useCallback(() => {
    if (interaction === 'buttons') return
    save()
  }, [interaction, save])

  const onCancel = useCallback(() => {
    if (!dirty) return
    resetProperty(name)
  }, [dirty, resetProperty, name])

  const onChange = useCallback(
    (e) => {
      onPropertyChange(name, e.target.value)
    },
    [name, onPropertyChange]
  )

  const onKeyDown = useCallback(
    async (e) => {
      switch (e.key) {
        case 'Enter':
          if (submitOnEnter) {
            await save()
          }
          break
        case 'Escape':
          resetProperty(name)
          e.stopPropagation()
          setTimeout(() => {
            e.target.blur()
          }, 0)
          break
      }
    },
    [name, resetProperty, save, submitOnEnter]
  )

  useLayoutEffect(() => {
    const node = anchorRef.current
    if (!node) {
      return
    }

    if (!isPopupMode) {
      const rect = node.getBoundingClientRect()
      setStoredRect(rect)
      setDimensions({ width: rect.width, height: rect.height })
      setPosition(null)
      return
    }

    const updatePosition = () => {
      const el = placeholderRef.current ?? anchorRef.current
      if (el) {
        setPosition(el.getBoundingClientRect())
      }
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isPopupMode, name, paragraph])

  useFocusTrap(anchorRef, isPopupMode)

  return (
    <>
      {isPopupMode && dimensions && (
        <Box
          ref={placeholderRef}
          aria-hidden
          sx={{
            width: dimensions.width,
            height: dimensions.height,
            flexShrink: 0,
          }}
        />
      )}
      <Stack
        ref={anchorRef}
        direction="row"
        spacing={1}
        {...rest}
        sx={{
          ...rest.sx,
          ...(elevatedRect
            ? {
                position: 'fixed',
                top: elevatedRect.top,
                left: elevatedRect.left,
                width: elevatedRect.width,
                zIndex: theme.zIndex.modal + 1,
              }
            : {}),
        }}
      >
        <Stack
          // alignItems="flex-end"
          spacing={0.5}
          sx={{ flexGrow: 1 }}
        >
          {render ? render({ onBlur, onChange, onKeyDown }) : children}
          {isPopupMode && (
            <Stack direction="row" spacing={0.5} sx={{ alignSelf: 'flex-end' }}>
              <ActionButton
                label={t('ui.cancel')}
                size="small"
                variant="outlined"
                onClick={onCancel}
              />
              <SubmitButton
                label={t('ui.save')}
                size="small"
                variant="contained"
                onClick={() => save()}
              />
              {!product && (
                <SubmitButton
                  label={t('ui.save-vib')}
                  size="small"
                  variant="contained"
                  onClick={() => save(true)}
                />
              )}
            </Stack>
          )}
        </Stack>
        {picto}
      </Stack>
      {isPopupMode && (
        <Portal>
          <Backdrop
            open
            sx={{
              zIndex: theme.zIndex.modal,
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            }}
            onClick={onCancel}
          />
        </Portal>
      )}
      <RestoreFromParentDialog
        diff={restoreDiff}
        open={restoreDiff !== null}
        onClose={() => setRestoreDiff(null)}
        onConfirm={handleRestoreConfirm}
      />
    </>
  )
}

Field.propTypes = {
  children: PropTypes.node.isRequired,
  editable: PropTypes.bool,
  name: PropTypes.string.isRequired,
  popupMode: PropTypes.bool,
  render: PropTypes.func,
  shift: PropTypes.bool,
  submitOnEnter: PropTypes.bool,
}

export default Field
